import { Head, Link, useForm, router } from '@inertiajs/react';
import { useState } from 'react';
import AdminLayout from '@/components/AdminLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { ArrowLeft, Plus, Edit, Trash2 } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';

interface FilterValue {
  id: number;
  value: string;
  active: boolean;
  position: number;
}

interface FilterCategory {
  id: number;
  name: string;
  key: string;
  description: string | null;
  active: boolean;
  position: number;
  filter_values: FilterValue[];
}

interface Props {
  filter_category: FilterCategory;
  filter_values: FilterValue[];
}

export default function FilterValuesIndex({ filter_category, filter_values }: Props) {
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [editingValue, setEditingValue] = useState<FilterValue | null>(null);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);

  const { data, setData, post, processing, reset } = useForm({
    value: '',
    active: true as boolean,
    position: 0,
  });

  const { data: editData, setData: setEditData, put, processing: editProcessing, reset: resetEdit } = useForm({
    value: '',
    active: true as boolean,
    position: 0,
  });

  const handleAddSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    post(`/admin/filter_categories/${filter_category.id}/filter_values`, {
      onSuccess: () => {
        setIsAddDialogOpen(false);
        reset();
      },
    });
  };

  const handleEditSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (editingValue) {
      put(`/admin/filter_categories/${filter_category.id}/filter_values/${editingValue.id}`, {
        onSuccess: () => {
          setIsEditDialogOpen(false);
          setEditingValue(null);
          resetEdit();
        },
      });
    }
  };

  const handleEdit = (value: FilterValue) => {
    setEditingValue(value);
    setEditData({
      value: value.value,
      active: value.active,
      position: value.position,
    });
    setIsEditDialogOpen(true);
  };

  const handleDelete = (valueId: number) => {
    if (confirm('Êtes-vous sûr de vouloir supprimer cette valeur ?')) {
      router.delete(`/admin/filter_categories/${filter_category.id}/filter_values/${valueId}`);
    }
  };

  return (
    <AdminLayout>
      <Head title={`Gérer les valeurs - ${filter_category.name}`} />
      
      <div className="space-y-6">
        <div className="flex items-center space-x-4">
          <Link href="/admin/filter_categories">
            <Button variant="outline" size="sm">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Retour
            </Button>
          </Link>
          <div>
            <h1 className="text-3xl font-bold">Gérer les valeurs</h1>
            <p className="text-gray-600 mt-2">
              Catégorie: <strong>{filter_category.name}</strong>
            </p>
          </div>
          <div className="ml-auto">
            <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
              <DialogTrigger asChild>
                <Button>
                  <Plus className="w-4 h-4 mr-2" />
                  Ajouter une valeur
                </Button>
              </DialogTrigger>
              <DialogContent className="p-0">
                <DialogHeader className="border-b border-gray-100 pb-4 px-6 pt-6">
                  <DialogTitle className="text-[#015963]">Ajouter une nouvelle valeur</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleAddSubmit} className="space-y-4 px-6 py-6">
                  <div className="space-y-2">
                    <Label htmlFor="value">Valeur *</Label>
                    <Input
                      id="value"
                      value={data.value}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => setData('value', e.target.value)}
                      placeholder="Ex: Kinésithérapie respiratoire"
                      required
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="position">Position</Label>
                      <Input
                        id="position"
                        type="number"
                        value={data.position}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setData('position', parseInt(e.target.value) || 0)}
                      />
                    </div>
                    <div className="flex items-center space-x-2">
                      <Switch
                        id="active"
                        checked={data.active}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setData('active', e.target.checked)}
                      />
                      <Label htmlFor="active">Active</Label>
                    </div>
                  </div>
                  <div className="flex justify-end space-x-3 pt-4 border-t border-gray-100">
                    <Button type="button" variant="outline" onClick={() => setIsAddDialogOpen(false)} className="text-[#015963] border-[#015963]/30 hover:bg-[#015963]/10">
                      Annuler
                    </Button>
                    <Button type="submit" disabled={processing} className="bg-[#008788] hover:bg-[#00bcbe] text-white">
                      {processing ? 'Ajout...' : 'Ajouter'}
                    </Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Valeurs de la catégorie</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {filter_values.length === 0 ? (
                <p className="text-gray-500 text-center py-8">
                  Aucune valeur définie pour cette catégorie.
                </p>
              ) : (
                filter_values.map((value) => (
                  <div key={value.id} className="flex items-center justify-between p-4 border rounded-lg">
                    <div className="flex items-center space-x-4">
                      <div>
                        <div className="flex items-center space-x-2">
                          <span className="font-medium">{value.value}</span>
                          <Badge variant={value.active ? "default" : "secondary"}>
                            {value.active ? "Actif" : "Inactif"}
                          </Badge>
                        </div>
                        <p className="text-sm text-gray-500">
                          Position: {value.position}
                        </p>
                      </div>
                    </div>
                    <div className="flex space-x-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleEdit(value)}
                      >
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleDelete(value.id)}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </CardContent>
        </Card>

        {/* Dialog d'édition */}
        <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
          <DialogContent className="p-0">
            <DialogHeader className="border-b border-gray-100 pb-4 px-6 pt-6">
              <DialogTitle className="text-[#015963]">Modifier la valeur</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleEditSubmit} className="space-y-4 px-6 py-6">
              <div className="space-y-2">
                <Label htmlFor="edit-value">Valeur *</Label>
                <Input
                  id="edit-value"
                  value={editData.value}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEditData('value', e.target.value)}
                  required
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="edit-position">Position</Label>
                  <Input
                    id="edit-position"
                    type="number"
                    value={editData.position}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEditData('position', parseInt(e.target.value) || 0)}
                  />
                </div>
                <div className="flex items-center space-x-2">
                  <Switch
                    id="edit-active"
                    checked={editData.active}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEditData('active', e.target.checked)}
                  />
                  <Label htmlFor="edit-active">Active</Label>
                </div>
              </div>
              <div className="flex justify-end space-x-3 pt-4 border-t border-gray-100">
                <Button type="button" variant="outline" onClick={() => setIsEditDialogOpen(false)} className="text-[#015963] border-[#015963]/30 hover:bg-[#015963]/10">
                  Annuler
                </Button>
                <Button type="submit" disabled={editProcessing} className="bg-[#008788] hover:bg-[#00bcbe] text-white">
                  {editProcessing ? 'Mise à jour...' : 'Mettre à jour'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
} 
